import { memo } from 'react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { getBoggleIntervalRemainingMs } from '~/boggle/lib/intervalTimer'
import { GradientText } from '~/boggle/ui/atoms/Text'
import { TimerBonusFeedbackLayer } from '~/boggle/ui/run/ScoreFeedback'
import type { useBoggleTimerBonusFeedback } from '~/boggle/ui/run/ScoreFeedback'
import { TimerGauge } from '~/boggle/ui/run/TimerGauge'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import classes from './RunTimerIsland.module.css'

function mergeClassNames(...classNames: Array<false | null | string | undefined>) {
  return classNames.filter(Boolean).join(' ')
}

/** Timer ring, gold-rush label, and layout placeholders — sole live timer subscription boundary for the HUD row. */
export const RunTimerIsland = memo(function RunTimerIsland({
  bufferedRunState,
  reserveRunLayout,
  showEndBuffer,
  showGoldRushTimerLabel,
  showMeterGaugeSibling,
  showTimerGauge,
  store,
  timerBonusFeedbackEvents,
  timerIntervalCount,
  timerIntervalDurationMs,
  timerIntervalsRemaining,
}: {
  bufferedRunState: BoggleGameState
  reserveRunLayout: boolean
  showEndBuffer: boolean
  showGoldRushTimerLabel: boolean
  /** When true, the meter gauge occupies the right slot (affects gold-rush label height). */
  showMeterGaugeSibling: boolean
  showTimerGauge: boolean
  store: BoggleStore
  timerBonusFeedbackEvents: ReturnType<typeof useBoggleTimerBonusFeedback>
  timerIntervalCount: null | number
  timerIntervalDurationMs: null | number
  timerIntervalsRemaining: null | number
}) {
  const timerGaugeActive =
    showTimerGauge && timerIntervalCount !== null && timerIntervalDurationMs !== null

  if (showGoldRushTimerLabel) {
    return (
      <div
        aria-label="Gold Rush"
        className={mergeClassNames(
          classes.goldRushLabel,
          showMeterGaugeSibling && classes.goldRushLabelWithMeter,
        )}
      >
        <span className={classes.goldRushText}>
          <GradientText
            color="#f6dd8a"
            duration={5}
            fz={40}
            fw={800}
            lh={1.05}
            tt="uppercase"
            pos="relative"
            top={4}
            left={10}
          >
            Gold Rush
          </GradientText>
        </span>
      </div>
    )
  }

  if (timerGaugeActive) {
    if (showEndBuffer) {
      const intervalRemainingMs = getBoggleIntervalRemainingMs({
        intervalDeadlineMs: bufferedRunState.intervalDeadlineMs,
        intervalDurationMs: timerIntervalDurationMs,
        intervalPlayBeginsAtMs: bufferedRunState.intervalPlayBeginsAtMs,
        isInOvertime: bufferedRunState.isInOvertime,
        timeMs: bufferedRunState.timeMs,
      })

      return (
        <div className={classes.feedbackAnchor}>
          <TimerGauge
            diameter={110}
            fontMetricsDiameter={112}
            progressMeterWidth={12}
            intervalCount={timerIntervalCount}
            intervalDurationMs={timerIntervalDurationMs}
            intervalIndex={
              timerIntervalsRemaining === null
                ? null
                : timerIntervalCount - timerIntervalsRemaining + 1
            }
            intervalRemainingMs={intervalRemainingMs}
          />
          <TimerBonusFeedbackLayer events={timerBonusFeedbackEvents} />
        </div>
      )
    }

    return (
      <LiveTimerGaugeSlot
        store={store}
        timerBonusFeedbackEvents={timerBonusFeedbackEvents}
        timerIntervalCount={timerIntervalCount}
        timerIntervalDurationMs={timerIntervalDurationMs}
        timerIntervalsRemaining={timerIntervalsRemaining}
      />
    )
  }

  if (reserveRunLayout) {
    return <div aria-hidden className={classes.gaugePlaceholder} />
  }

  return null
})

const LiveTimerGaugeSlot = memo(function LiveTimerGaugeSlot({
  store,
  timerBonusFeedbackEvents,
  timerIntervalCount,
  timerIntervalDurationMs,
  timerIntervalsRemaining,
}: {
  store: BoggleStore
  timerBonusFeedbackEvents: ReturnType<typeof useBoggleTimerBonusFeedback>
  timerIntervalCount: number
  timerIntervalDurationMs: number
  timerIntervalsRemaining: null | number
}) {
  return (
    <div className={classes.feedbackAnchor}>
      <TimerGauge
        diameter={110}
        fontMetricsDiameter={112}
        progressMeterWidth={12}
        intervalCount={timerIntervalCount}
        intervalDurationMs={timerIntervalDurationMs}
        intervalIndex={
          timerIntervalsRemaining === null
            ? null
            : timerIntervalCount - timerIntervalsRemaining + 1
        }
        store={store}
      />
      <TimerBonusFeedbackLayer events={timerBonusFeedbackEvents} />
    </div>
  )
})
